<template>
  <div class="materials-admin">
    <!-- Status / sincronização -->
    <div class="card">
      <div
        class="card-body d-flex flex-wrap align-items-center"
        style="gap: 16px"
      >
        <div>
          <div
            class="text-muted"
            style="font-size: 12px"
          >
            Última sincronização
          </div>
          <div style="font-weight: 600">
            {{ status && status.last_sync_at ? formatDate(status.last_sync_at) : 'Nunca sincronizado' }}
            <span
              v-if="status && status.last_sync_status"
              class="badge ml-50"
              :class="statusBadgeClass"
            >
              {{ statusLabel }}
            </span>
          </div>
          <div
            v-if="status && status.last_sync_message"
            class="text-danger"
            style="font-size: 11px"
          >
            {{ status.last_sync_message }}
          </div>
        </div>
        <div>
          <div
            class="text-muted"
            style="font-size: 12px"
          >
            Materiais ativos
          </div>
          <div style="font-weight: 600">
            {{ status ? status.materials_count : '—' }}
          </div>
        </div>
        <div
          class="ml-auto d-flex"
          style="gap: 8px"
        >
          <button
            v-if="$hasAbility('node.admin')"
            type="button"
            class="btn btn-primary btn-sm"
            :disabled="syncing || !(status && status.enabled)"
            @click="syncNow(false)"
          >
            <span
              v-if="syncing"
              class="spinner-border spinner-border-sm mr-50"
            />
            <feather-icon
              v-else
              icon="RefreshCwIcon"
              size="14"
              class="mr-50"
            />
            Sincronizar agora
          </button>
          <button
            v-if="$hasAbility('node.admin')"
            type="button"
            class="btn btn-outline-secondary btn-sm"
            :disabled="syncing || !(status && status.enabled)"
            title="Recarrega todo o catálogo (ignora a marca-d'água)"
            @click="syncNow(true)"
          >
            Sincronização completa
          </button>
        </div>
      </div>
      <div
        v-if="status && !status.enabled"
        class="card-footer text-warning"
        style="font-size: 12px"
      >
        Integração DIM não está ativa para este nó. Configure na aba "Materiais (DIM)" da administração do nó.
      </div>
    </div>

    <!-- Catálogo -->
    <div class="card">
      <div class="card-body">
        <div
          v-if="$hasAbility('node.ver')"
          class="d-flex flex-wrap align-items-end mb-1"
          style="gap: 12px"
        >
          <div style="flex: 1; min-width: 220px">
            <label style="font-size: 12px">Buscar (código ou nome)</label>
            <input
              v-model="filters.q"
              class="form-control"
              type="text"
              placeholder="Digite código ou nome..."
              @input="onSearch"
            >
          </div>
          <div>
            <label style="font-size: 12px">Situação</label>
            <select
              v-model="filters.active"
              class="form-control"
              @change="reload"
            >
              <option value="1">
                Ativos
              </option>
              <option value="0">
                Inativos
              </option>
              <option value="">
                Todos
              </option>
            </select>
          </div>
        </div>

        <div
          v-if="loading"
          class="text-center py-3"
        >
          <span class="spinner-border spinner-border-sm" /> Carregando...
        </div>

        <div
          v-else
          class="table-responsive"
        >
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Código</th>
                <th>Material</th>
                <th>Aba</th>
                <th>Unidade</th>
                <th class="text-center">
                  Situação
                </th>
                <th class="text-center">
                  CBOs
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="m in materials"
                :key="m.id"
              >
                <td class="text-nowrap">
                  {{ m.code || '—' }}
                </td>
                <td>
                  {{ m.name }}
                  <span
                    v-if="m.controlled"
                    class="badge badge-light-danger ml-50"
                  >controlado</span>
                </td>
                <td>{{ m.tab ? m.tab.title : '—' }}</td>
                <td>{{ m.unit || '—' }}</td>
                <td class="text-center">
                  <span
                    class="badge"
                    :class="m.active ? 'badge-light-success' : 'badge-light-secondary'"
                  >
                    {{ m.active ? 'Ativo' : 'Inativo' }}
                  </span>
                </td>
                <td class="text-center">
                  <span
                    v-if="m.cbos && m.cbos.length"
                    class="badge badge-light-primary"
                  >
                    {{ m.cbos.length }}
                  </span>
                  <span
                    v-else
                    class="text-muted"
                    style="font-size: 11px"
                  >todos</span>
                </td>
                <td class="text-right">
                  <button
                    v-if="$hasAbility('node.admin')"
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    @click="openCbos(m)"
                  >
                    <feather-icon
                      icon="UsersIcon"
                      size="14"
                      class="mr-25"
                    />
                    CBOs
                  </button>
                </td>
              </tr>
              <tr v-if="!materials.length">
                <td
                  colspan="7"
                  class="text-center text-muted py-2"
                >
                  Nenhum material encontrado.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginação simples -->
        <div
          v-if="$hasAbility('node.ver')"
          class="d-flex align-items-center justify-content-between mt-1"
        >
          <small class="text-muted">{{ total }} materiais</small>
          <div
            class="d-flex align-items-center"
            style="gap: 8px"
          >
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              :disabled="page <= 1"
              @click="changePage(page - 1)"
            >
              Anterior
            </button>
            <small>Página {{ page }} de {{ lastPage }}</small>
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              :disabled="page >= lastPage"
              @click="changePage(page + 1)"
            >
              Próxima
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: vincular CBOs (b-modal é global via ModalPlugin) -->
    <b-modal
      v-model="showCboModal"
      title="Restrição por CBO"
      ok-title="Salvar"
      cancel-title="Cancelar"
      centered
      :busy="savingCbos"
      @ok.prevent="saveCbos"
    >
      <p style="font-size: 13px">
        <strong>{{ cboMaterial ? cboMaterial.name : '' }}</strong><br>
        <span
          class="text-muted"
          style="font-size: 12px"
        >
          Sem CBO vinculado = liberado a todos. Com CBO = só os CBOs selecionados podem prescrever.
        </span>
      </p>
      <v-select
        v-model="selectedCbos"
        :options="cboCatalog"
        :reduce="(c) => c.id"
        label="cbo_name"
        multiple
        :loading="loadingCboCatalog"
        placeholder="Selecione os CBOs (vazio = todos)"
      />
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'MaterialsAdmin',
  data() {
    return {
      nodeId: this.$route.params.nodeId,
      status: null,
      syncing: false,
      loading: false,
      materials: [],
      total: 0,
      page: 1,
      perPage: 25,
      filters: { q: '', active: '1' },
      searchTimer: null,
      // CBOs
      showCboModal: false,
      cboMaterial: null,
      cboCatalog: [],
      loadingCboCatalog: false,
      selectedCbos: [],
      savingCbos: false,
    }
  },
  computed: {
    lastPage() {
      return Math.max(1, Math.ceil(this.total / this.perPage))
    },
    statusBadgeClass() {
      const s = this.status && this.status.last_sync_status
      if (s === 'success') return 'badge-light-success'
      if (s === 'error') return 'badge-light-danger'
      if (s === 'running') return 'badge-light-warning'
      return 'badge-light-secondary'
    },
    statusLabel() {
      const map = { success: 'Sucesso', error: 'Erro', running: 'Em andamento' }
      return map[this.status && this.status.last_sync_status] || '—'
    },
  },
  mounted() {
    this.fetchStatus()
    this.reload()
  },
  methods: {
    formatDate(val) {
      if (!val) return '—'
      return new Date(val).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    },
    fetchStatus() {
      this.$http
        .get(`${this.$store.state.api}materials/sync/status`, { params: { node_id: this.nodeId } })
        .then(r => { this.status = r.data.data })
        .catch(() => {})
    },
    fetchMaterials() {
      this.loading = true
      this.$http
        .get(`${this.$store.state.api}materials`, {
          params: {
            node_id: this.nodeId,
            page: this.page,
            limit: this.perPage,
            q: this.filters.q || undefined,
            active: this.filters.active !== '' ? this.filters.active : undefined,
          },
        })
        .then(r => {
          this.materials = r.data.data || []
          this.total = r.data.count || 0
        })
        .catch(() => { this.materials = []; this.total = 0 })
        .finally(() => { this.loading = false })
    },
    reload() {
      this.page = 1
      this.fetchMaterials()
    },
    changePage(p) {
      this.page = p
      this.fetchMaterials()
    },
    onSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.reload(), 400)
    },
    syncNow(full) {
      this.syncing = true
      this.$http
        .post(`${this.$store.state.api}materials/sync`, { node_id: this.nodeId, full })
        .then(r => {
          if (r.data && r.data.ok) {
            const c = r.data.counters || {}
            this.$message('Sincronizado', `${c.materials || 0} materiais, ${c.tabs || 0} abas.`, 'success')
            this.status = r.data.status
            this.fetchMaterials()
          } else {
            this.$message('Atenção', (r.data && r.data.message) || 'Sincronização não concluída.', 'warning')
          }
        })
        .catch(e => {
          const msg = e.response && e.response.data && e.response.data.message
            ? e.response.data.message : 'Falha ao sincronizar.'
          this.$message('Erro', msg, 'error')
          if (e.response && e.response.data && e.response.data.status) this.status = e.response.data.status
        })
        .finally(() => { this.syncing = false })
    },
    loadCboCatalog() {
      if (this.cboCatalog.length) return
      this.loadingCboCatalog = true
      this.$http
        .get(`${this.$store.state.api}cbos`, { params: { paginated: false } })
        .then(r => { this.cboCatalog = r.data.data || r.data || [] })
        .catch(() => { this.cboCatalog = [] })
        .finally(() => { this.loadingCboCatalog = false })
    },
    openCbos(material) {
      this.cboMaterial = material
      this.selectedCbos = (material.cbos || []).map(c => c.id)
      this.loadCboCatalog()
      this.showCboModal = true
    },
    saveCbos() {
      this.savingCbos = true
      this.$http
        .post(`${this.$store.state.api}materials/${this.cboMaterial.id}/cbos`, {
          node_id: this.nodeId,
          cbo_ids: this.selectedCbos,
        })
        .then(r => {
          const cbos = r.data.data || []
          this.$set(this.cboMaterial, 'cbos', cbos)
          this.showCboModal = false
          this.$message('Sucesso', 'CBOs atualizados.', 'success')
        })
        .catch(() => this.$message('Erro', 'Não foi possível salvar os CBOs.', 'error'))
        .finally(() => { this.savingCbos = false })
    },
  },
}
</script>
